import { chain, mergeRefs } from '@react-aria/utils';
import { useControlledState } from '@react-stately/utils';
import type { ForwardedRef, ReactElement, Ref } from 'react';
import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useEffect,
  useRef,
} from 'react';
import type {
  PressEvent,
  TooltipProps as AriaTooltipProps,
  TooltipRenderProps,
  TooltipTriggerComponentProps as AriaTooltipTriggerProps,
} from 'react-aria-components';
import {
  OverlayArrow,
  Provider,
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
  composeRenderProps,
} from 'react-aria-components';

import { colors } from '@infinex/ui-constants';
import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn } from '@infinex/ui-styles';

import { KbdContext } from '../content';

export type TooltipProps = Pick<AriaTooltipProps, 'placement' | 'children'> &
  UnsafeStyleProps;

/**
 * Tooltips show contextual help or information, on hover and focus, that
 * concisely describes the function of their associated element.
 *
 * [Taurus docs: `Tooltip`](https://ui-sb.app.infinex.dev/?path=/docs/components-tooltip--docs)
 */
export const Tooltip = forwardRef(function Tooltip(
  props: TooltipProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { children, className, ...otherProps } = props;

  return (
    <Provider values={[[KbdContext, { className: 'ml-[1ch]' }]]}>
      <AriaTooltip
        {...otherProps}
        ref={ref}
        className={cn(
          'bg-canvasInverse max-w-48 rounded-sm px-2 py-1 shadow-sm',
          'text-inverse text-xs font-medium text-pretty',
          // common animations
          'entering:animate-in entering:fade-in entering:ease-out',
          'exiting:animate-out exiting:fade-out',
          // placement animations
          'placement-bottom:entering:slide-in-from-top-2 placement-bottom:mt-2',
          'placement-top:entering:slide-in-from-bottom-2 placement-top:mb-2',
          'placement-left:entering:slide-in-from-right-2 placement-left:mr-2',
          'placement-right:entering:slide-in-from-left-2 placement-right:ml-2',
          className
        )}
      >
        {composeRenderProps(children, (content, renderProps) => (
          <>
            <TooltipArrow {...renderProps} />
            {content}
          </>
        ))}
      </AriaTooltip>
    </Provider>
  );
});

// Arrow
// -----------------------------------------------------------------------------

function TooltipArrow(props: TooltipRenderProps) {
  const { placement } = props;

  return (
    <OverlayArrow>
      <svg
        width={10}
        height={10}
        viewBox="0 0 10 10"
        fill={colors.background.canvasInverse}
        className={cn({
          'rotate-90': placement === 'right',
          'rotate-180': placement === 'bottom',
          '-rotate-90': placement === 'left',
        })}
      >
        <path d="M0 0 L5 5 L10 0" />
      </svg>
    </OverlayArrow>
  );
}

// Trigger
// -----------------------------------------------------------------------------

export type TooltipTriggerProps = Omit<
  AriaTooltipTriggerProps,
  'closeDelay' | 'delay' | 'trigger'
> & {
  /**
   * Controls tap-to-toggle on touch devices.
   *
   * @default true
   */
  isTouchOpenEnabled?: boolean;
};

// Needs to be enough time to ensure intent, without making the user wait too
// long. Informed by: https://www.nngroup.com/articles/timing-exposing-content
const MOUSE_OPEN_DELAY = 600;
const MOUSE_CLOSE_DELAY = 500;

// The trigger element, narrowed to the props we augment for touch support.
type TriggerElement = ReactElement<{
  onPress?: (event: PressEvent) => void;
  ref?: Ref<HTMLElement>;
}>;

/**
 * A controller for the `Tooltip`, bound to an associated trigger element; it
 * handles opening and closing when the user hovers over or focuses the trigger.
 *
 * Touch devices have neither hover nor focus, so React Aria deliberately never
 * opens tooltips on touch (see adobe/react-spectrum#1136). For help-style
 * triggers — whose only purpose is to reveal the tooltip — we opt into
 * tap-to-toggle: tapping the trigger toggles the tooltip, and tapping elsewhere
 * or scrolling dismisses it. Hover and focus on pointer/keyboard devices flow
 * through React Aria untouched.
 */
export function TooltipTrigger(props: TooltipTriggerProps) {
  const {
    children,
    isTouchOpenEnabled,
    isOpen: isOpenProp,
    defaultOpen,
    onOpenChange,
    ...otherProps
  } = props;

  const [isOpen, setIsOpen] = useControlledState(
    isOpenProp,
    defaultOpen ?? false,
    onOpenChange
  );

  const triggerRef = useRef<HTMLElement>(null);
  // Tracks whether the current open was initiated by a touch tap, so the
  // dismiss listeners below never interfere with desktop hover/focus opens.
  const openedByTouch = useRef(false);

  // While open via touch, dismiss on any interaction outside the trigger. The
  // trigger itself is excluded so its own tap toggles through `onPress` below.
  useEffect(() => {
    if (!isOpen) {
      openedByTouch.current = false;
      return;
    }
    if (!openedByTouch.current) return;

    const dismiss = (event: Event) => {
      const target = event.target as Node | null;
      if (target && triggerRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    const scrollListenerOptions = { capture: true, passive: true };
    document.addEventListener('pointerdown', dismiss, true);
    document.addEventListener('scroll', dismiss, scrollListenerOptions);
    return () => {
      document.removeEventListener('pointerdown', dismiss, true);
      document.removeEventListener('scroll', dismiss, scrollListenerOptions);
    };
  }, [isOpen, setIsOpen]);

  const [trigger, ...overlay] = Children.toArray(children);
  const shouldEnableTouchOpen = isTouchOpenEnabled ?? true;
  const enhancedTrigger =
    shouldEnableTouchOpen && !otherProps.isDisabled && isValidElement(trigger)
      ? cloneElement(trigger as TriggerElement, {
          ref: mergeRefs(triggerRef, (trigger as TriggerElement).props.ref),
          onPress: chain(
            (trigger as TriggerElement).props.onPress,
            (event: PressEvent) => {
              if (event.pointerType !== 'touch') return;
              if (!isOpen) openedByTouch.current = true;
              setIsOpen(!isOpen);
            }
          ),
        })
      : trigger;

  return (
    <AriaTooltipTrigger
      {...otherProps}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      delay={MOUSE_OPEN_DELAY}
      closeDelay={MOUSE_CLOSE_DELAY}
    >
      {enhancedTrigger}
      {overlay}
    </AriaTooltipTrigger>
  );
}
